// src/app/services/mbrs/equality-message.ts

/** The constant phrase every mismatch message shares. Exported so sheet
 *  components can discriminate a mismatch message from other validation text
 *  (e.g. an extracted-sum suggested-change) without re-hardcoding the wording —
 *  keeping producer and consumers tied to ONE source of truth. */
export const MISMATCH_CORE = 'Mismatch in value provided for the same field.';

/** Parts that compose a field-mismatch message. */
export interface MismatchMessageParts {
  /** Column label prefix, e.g. "Separate Current". Omit for the bare core (used by generateEqualityMessage). */
  columnLabel?: string;
  /** Optional second prefix segment, e.g. an equity-column label. */
  qualifier?: string;
  /** Resolved human-readable label of the field to match against. */
  fieldLabel: string;
  /** Optional "(under section …)" qualifier appended to the field label. */
  section?: string;
  /** Display name of the sheet that holds the reference field. */
  sheetLabel: string;
}

/**
 * Strip the namespace prefix from a qname so a raw, prefixed qname
 * (e.g. "ifrs-full:ProfitLoss") never surfaces in a user-facing message.
 * Returns the input unchanged when there is no ':' prefix.
 */
export function humanizeQname(qname: string): string {
  return qname.split(':').pop() || qname;
}

/**
 * Assemble a "Mismatch in value provided for the same field…" message from its
 * parts. Single source of truth for the wording across every sheet.
 */
export function buildMismatchMessage(parts: MismatchMessageParts): string {
  const prefixSegments = [parts.columnLabel, parts.qualifier].filter(
    (message): message is string => !!message
  );
  const prefix = prefixSegments.length ? `${prefixSegments.join(': ')}: ` : '';

  const field = parts.section
    ? `${parts.fieldLabel} (under section ${parts.section})`
    : parts.fieldLabel;

  return `${prefix}${MISMATCH_CORE} The value should match ${field} in ${parts.sheetLabel}.`;
}
